import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Chip } from "../../components/ui/Chip";
import { errorMessage, recordedLabel } from "../cockpit/format";
import { formatCoordinates, verdictLabel, verdictVariant } from "./format";
import { applyAddressPoint, isRecentCheck, lastLocationCheck, runLocationCheck } from "./locationApi";
import type { FacilityDetail, LocationCheckResult, LocationCheckSummary } from "./types";

interface Props {
  registryId: string;
  canCheck: boolean;
  autoRun?: boolean;
  onApplied?: (detail: FacilityDetail) => void;
  onUseName?: (name: string) => void;
}

function miles(value: number): string {
  return value < 10 ? value.toFixed(2) : value.toFixed(1);
}

export function LocationCheckPanel({ registryId, canCheck, autoRun = false, onApplied, onUseName }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;
  const [last, setLast] = useState<LocationCheckSummary | null | undefined>(undefined);
  const [result, setResult] = useState<LocationCheckResult | null>(null);
  const [busy, setBusy] = useState<"check" | "move" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [moved, setMoved] = useState<{ from: string | null; to: string } | null>(null);

  const check = useCallback(async () => {
    setBusy("check");
    setError(null);
    setConfirming(false);
    setMoved(null);
    try {
      setResult(await runLocationCheck(apiFetchRef.current, registryId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [registryId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let summary: LocationCheckSummary | null;
      try {
        summary = await lastLocationCheck(apiFetchRef.current, registryId);
      } catch (err) {
        if (!cancelled) {
          setLast(null);
          setError(errorMessage(err));
        }
        return;
      }
      if (cancelled) return;
      setLast(summary);
      if (autoRun && canCheck && (!summary || !isRecentCheck(summary.checkedUtc))) await check();
    })();
    return () => {
      cancelled = true;
    };
  }, [registryId, autoRun, canCheck, check]);

  const move = async () => {
    const checkId = result?.checkId ?? last?.checkId;
    if (checkId === undefined) return;
    setBusy("move");
    setError(null);
    try {
      const detail = await applyAddressPoint(apiFetchRef.current, registryId, checkId);
      const before = result?.storedPoint;
      setMoved({
        from: before ? formatCoordinates(before.latitude, before.longitude) : null,
        to: formatCoordinates(detail.latitude, detail.longitude),
      });
      setConfirming(false);
      onAppliedRef.current?.(detail);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const verdict = result?.verdict ?? last?.verdict;
  const canMove = !moved && (result ? result.canUseAddressPoint : (last?.canUseAddressPoint ?? false));
  const addressDistance = result?.addressPoint?.distanceMiles ?? last?.addressDistanceMiles ?? null;
  const used = result?.monthlyUsed ?? last?.monthlyUsed;
  const limit = result?.monthlyLimit ?? last?.monthlyLimit;

  return (
    <div className="mb-5 text-[13px]" aria-label="Location check">
      <h4 className="m-0 mb-2 text-sm">Location Check</h4>

      {last === undefined && !error && <p className="m-0 text-rs-light-gray">Loading…</p>}

      {verdict && (
        <p className="m-0 mb-2 flex flex-wrap items-center gap-2">
          <Chip variant={verdictVariant(verdict)}>{verdictLabel(verdict)}</Chip>
          {!result && last && (
            <span className="text-rs-light-gray">
              Checked {recordedLabel(last.checkedUtc)}
              {last.checkedByName ? ` by ${last.checkedByName}` : ""}
            </span>
          )}
        </p>
      )}

      {result && <p className="m-0 mb-2">{result.summary}</p>}

      {addressDistance !== null && (
        <p className="m-0 mb-2 text-rs-light-gray">
          The address is {miles(addressDistance)} mi from the stored point
          {(result?.addressPoint?.confidence ?? last?.addressConfidence)
            ? ` (${result?.addressPoint?.confidence ?? last?.addressConfidence} confidence)`
            : ""}
          .
        </p>
      )}

      {result && result.businesses.length > 0 && (
        <>
          <p className="m-0 mb-1 text-rs-light-gray">Businesses listed on the map nearby</p>
          <ul className="m-0 mb-3 p-0 list-none flex flex-col gap-1">
            {result.businesses.map((business) => (
              <li key={`${business.near}-${business.name}-${business.distanceMiles}`}>
                <span className={business.matches ? "text-rs-green font-semibold" : ""}>{business.name}</span>
                {business.matches && (
                  <Chip className="ml-2" variant="ok">
                    Similar name
                  </Chip>
                )}
                <span className="text-rs-light-gray">
                  {" "}
                  · {miles(business.distanceMiles)} mi from the{" "}
                  {business.near === "Stored" ? "stored point" : "address"}
                </span>
                {onUseName && canCheck && (
                  <button
                    type="button"
                    className="bg-transparent border-none p-0 ml-2 text-rs-green"
                    aria-label={`Use the name ${business.name}`}
                    onClick={() => onUseName(business.name)}
                  >
                    Use name
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {result && result.businesses.length === 0 && result.verdict !== "NoLocation" && (
        <p className="m-0 mb-2 text-rs-light-gray">No businesses are listed within half a mile.</p>
      )}

      {!verdict && last === null && !busy && !error && (
        <p className="m-0 mb-2 text-rs-light-gray">Not checked against the map yet.</p>
      )}

      {moved && (
        <p className="m-0 mb-2 text-rs-green" role="status">
          The point now uses the address location
          {moved.from ? `, moved from ${moved.from} to ${moved.to}` : `: ${moved.to}`}. The old point is kept on the
          check record.
        </p>
      )}

      {error && <p className="m-0 mb-2 text-rs-red">{error}</p>}
      {busy === "check" && <p className="m-0 mb-2 text-rs-light-gray">Checking the map…</p>}

      {confirming && (
        <p className="m-0 mb-2">
          Move this plant's point{addressDistance !== null ? ` ${miles(addressDistance)} mi` : ""} to the address
          location?{" "}
          <Btn className="ml-1" disabled={busy !== null} onClick={() => void move()}>
            Move Point
          </Btn>{" "}
          <Btn variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Btn>
        </p>
      )}

      {canCheck && !confirming && (
        <div className="flex flex-wrap items-center gap-2">
          <Btn variant="ghost" disabled={busy !== null || last === undefined} onClick={() => void check()}>
            <span className="material-symbols-sharp">travel_explore</span>
            {verdict ? "Check Again" : "Check Location"}
          </Btn>
          {canMove && (
            <Btn variant="ghost" disabled={busy !== null} onClick={() => setConfirming(true)}>
              <span className="material-symbols-sharp">pin_drop</span>Use Address Location
            </Btn>
          )}
          <span className="text-rs-light-gray">Uses 2 to 4 map lookups.</span>
        </div>
      )}

      {used !== undefined && limit !== undefined && (
        <p className="m-0 mt-2 text-xs text-rs-light-gray">
          {used.toLocaleString()} of {limit.toLocaleString()} map lookups used this month.
        </p>
      )}
    </div>
  );
}
